import { useCallback } from 'react';

import { useChatAnalytics } from 'app/analytics';
import { Box, Flex } from 'shared';
import { useCopyToClipboard } from 'shared_DEPRECATED/hooks';
import { Nullable } from 'shared_DEPRECATED/types';
import { Attachment } from 'shared_DEPRECATED/types/Attachment';
import { dateUtils } from 'shared_DEPRECATED/utils';
import { LinkPreview } from 'types/common';

import {
  ChatMessageUnuploadedAttachments,
  ChatMessageUploadedAttachments,
} from 'features/chat/components/ChatMessages';
import { ChatMessageText } from 'features/chat/components/ChatMessages/ChatMessage/Text';
import {
  ScheduledChatMessageActionsMenu,
  ScheduledForWrapper,
  ScheduledMessageParticipantTime,
} from 'features/chat/components/ChatMessagesScheduling/ScheduledMessage/';
import { UnuploadedAttachment } from 'features/chat/config/types/Attachment';
import { useChatInterlocutorContext } from 'features/chat/context/chatInterlocutor';
import { useChatMessagesContext } from 'features/chat/context/chatMessages';
import { useChatMessageScheduleDelete } from 'features/chat/hooks/mutation/useChatMessageScheduleDelete';
import { useChatMessageScheduleEdit } from 'features/chat/hooks/mutation/useChatMessageScheduleEdit';
import { useChatMessageScheduleSend } from 'features/chat/hooks/mutation/useChatMessageScheduleSend';
import { useScheduledMessageDeleteConfirmation } from 'features/chat/hooks/useScheduledChatMessageDelete';
import { useShowScheduledModal } from 'features/chat/hooks/useShowScheduledModal';
import { useParticipantTimeZone } from 'features/participants/hooks/useParticipantTimeZone';

import { Show } from 'shared/components/Show';

import { ChatMessageLinkPreviewUploaded } from '../ChatMessages/LinkPreviewUploaded';

type ScheduledChatMessageProps = {
  messageId: string;
  text: string;
  scheduledFor: string;
  divider: string;
  attachments: Attachment[] | UnuploadedAttachment[];
  isTemporary: boolean;
  linkPreview: Nullable<LinkPreview>;
};

export const ScheduledChatMessage = ({
  messageId,
  text,
  scheduledFor,
  divider,
  attachments,
  isTemporary,
  linkPreview,
}: ScheduledChatMessageProps) => {
  const { mutateAsync: deleteChatMessage } = useChatMessageScheduleDelete();
  const { mutateAsync: editChatMessage } = useChatMessageScheduleEdit();
  const { name } = useChatInterlocutorContext();
  const timeZone = useParticipantTimeZone();
  const { setEditedMessage } = useChatMessagesContext();
  const showScheduleModal = useShowScheduledModal();
  const {
    sendChatMessageRescheduledAnalyticsEvent,
    sendChatMessageSendNowAnalyticsEvent,
  } = useChatAnalytics();

  const { mutateAsync: chatMessageSend } = useChatMessageScheduleSend({
    onSuccess: sendChatMessageSendNowAnalyticsEvent,
  });

  const handleSendNowMessage = useCallback(() => {
    chatMessageSend(messageId);
  }, [chatMessageSend, messageId]);

  const handleDeleteMessage = useCallback(() => {
    deleteChatMessage(messageId);
  }, [messageId, deleteChatMessage]);

  const handleRescheduleMessage = useCallback(
    (newScheduledFor: string) => {
      editChatMessage({
        messageId,
        text,
        scheduledFor: dateUtils(
          dateUtils.utc(newScheduledFor)
        ).formatAsISOString(),
        attachments,
        linkPreview,
      });
      sendChatMessageRescheduledAnalyticsEvent();
    },
    [
      messageId,
      text,
      attachments,
      editChatMessage,
      sendChatMessageRescheduledAnalyticsEvent,
      linkPreview,
    ]
  );

  const handleEditText = useCallback(() => {
    setEditedMessage({
      messageId,
      fromUserId: '',
      text,
      attachments,
      scheduledFor,
      deleted: false,
      linkPreview,
    });
  }, [
    setEditedMessage,
    messageId,
    text,
    scheduledFor,
    attachments,
    linkPreview,
  ]);

  const handleCopyToClipboard = useCopyToClipboard({
    clipboardText: text,
    clipboardTextName: 'message text',
  });

  const handleReschedule = useCallback(() => {
    showScheduleModal(
      handleRescheduleMessage,
      dateUtils.utc(scheduledFor).toString()
    );
    sendChatMessageRescheduledAnalyticsEvent();
  }, [
    scheduledFor,
    handleRescheduleMessage,
    sendChatMessageRescheduledAnalyticsEvent,
    showScheduleModal,
  ]);

  const confirmMessageDelete = useScheduledMessageDeleteConfirmation({
    onConfirm: handleDeleteMessage,
    interlocutorName: name,
    scheduledFor,
  });

  return (
    <Box>
      <ScheduledForWrapper divider={divider}>
        <Flex
          gap=".5rem"
          pb={4}
          w="100%"
          alignItems="center"
          justifyContent="end"
        >
          <ScheduledChatMessageActionsMenu
            onMessageSendNow={handleSendNowMessage}
            onConfirmMessageDelete={confirmMessageDelete}
            onMessageReschedule={handleReschedule}
            onCopyToClipboard={handleCopyToClipboard}
            onTextEdit={handleEditText}
          />
          <Box
            display="inline-block"
            maxW="25rem"
            wordBreak="break-all"
            overflow="hidden"
            p="2.5"
            gap={2.5}
            borderRadius="12px 12px 0 12px"
            color="var(--white)"
            bg="var(--bgCompAccent)"
          >
            <Show
              when={!!attachments?.length}
              fallback={
                <>
                  <Show when={!!linkPreview?.url.length}>
                    {() => (
                      <ChatMessageLinkPreviewUploaded
                        title={linkPreview!.title}
                        description={linkPreview!.description}
                        image={linkPreview!.imageMetadata.url}
                        url={linkPreview!.url}
                      />
                    )}
                  </Show>
                </>
              }
            >
              <Box>
                {isTemporary ? (
                  <ChatMessageUnuploadedAttachments
                    attachments={attachments as UnuploadedAttachment[]}
                  />
                ) : (
                  <ChatMessageUploadedAttachments
                    attachments={attachments as Attachment[]}
                  />
                )}
              </Box>
            </Show>
            <ChatMessageText
              text={text}
              isLinkPreviewPresent={!!linkPreview?.url && !attachments?.length}
            />
            <ScheduledMessageParticipantTime>
              {dateUtils.utc(scheduledFor).tz(timeZone).format('h:mm a')}
            </ScheduledMessageParticipantTime>
          </Box>
        </Flex>
      </ScheduledForWrapper>
    </Box>
  );
};
